import type { ComponentRequirements } from '@vibesites/tokens';
import { defineComponentRequirements } from '@vibesites/tokens';

export const componentRequirements: Record<string, ComponentRequirements> = {
  Header: defineComponentRequirements({
    tokens: ['color.primary', 'color.secondary', 'color.accent', 'color.text', 'color.textMuted', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    fields: ['property_phone', 'property_call_tracking_phone', 'property_full_address', 'property_google_place_url', 'property_mono_black_logo', 'property_mono_white_logo', 'property_secondary_logo', 'property_primary_logo', 'property_apply_now_url', 'property_resident_portal_url', 'property_facebook_url', 'property_instagram_url', 'property_featured_image'],
    collections: ['virtual_tours', 'photo_gallery'],
  }),
  Hero: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'font.heading', 'font.body'],
    fields: ['property_short_description', 'property_floorplans_url', 'property_bedrooms_range', 'property_address_city', 'property_address_state'],
    collections: ['photo_gallery', 'virtual_tours'],
  }),
  PropertyOverview: defineComponentRequirements({
    tokens: ['color.primary', 'color.accent', 'color.text', 'color.textOnPrimary', 'color.background', 'font.heading', 'font.body'],
    fields: ['property_home_amenities_blurb'],
    collections: ['amenities'],
  }),
  SpecialOffers: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    fields: ['property_website_url'],
    collections: ['photo_gallery', 'special_offers'],
  }),
  CTASection: defineComponentRequirements({
    tokens: ['color.text', 'color.surface', 'font.heading', 'font.body'],
    fields: ['property_name', 'property_address_city'],
    collections: ['photo_gallery'],
  }),
  NeighborhoodSection: defineComponentRequirements({
    tokens: ['color.primary', 'color.secondary', 'color.text', 'color.textMuted', 'font.heading', 'font.body'],
    fields: ['property_home_neighborhood_blurb'],
    collections: ['photo_gallery'],
  }),
  FloorplansList: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'font.heading', 'font.body'],
    collections: ['floorplans', 'photo_gallery'],
  }),
  PetPolicy: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.textMuted', 'color.surface', 'color.border', 'color.background', 'font.heading', 'font.body'],
    fields: [
      'property_ispetfriendly', 'property_allows_cats', 'property_allows_dogs',
      'property_max_pets', 'property_max_pet_weight_limit',
      'property_pet_deposit', 'property_upfront_pet_fee', 'property_monthly_pet_rent',
      'property_pet_restrictions', 'property_pet_policy_description', 'property_petpolicy_info',
    ],
  }),
  AmenitiesList: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    collections: ['amenities', 'photo_gallery'],
  }),
  PhotoGallery: defineComponentRequirements({
    tokens: ['color.text', 'font.body'],
    collections: ['photo_gallery'],
  }),
  NeighborhoodMap: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    fields: ['property_map_image', 'property_neighborhood_description'],
    collections: ['neighborhood_pois'],
  }),
  VirtualTourEmbed: defineComponentRequirements({
    tokens: ['color.text', 'color.surface', 'font.heading', 'font.body'],
    fields: ['property_virtual_tour_url'],
  }),
  ContactForm: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'font.heading', 'font.body'],
    fields: ['property_phone', 'property_address', 'property_city_state_zip', 'property_email'],
    collections: ['photo_gallery'],
  }),
  ContactUs: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'color.background', 'font.heading', 'font.body'],
    fields: ['property_phone', 'property_call_tracking_phone', 'property_full_address'],
    collections: ['office_hours'],
  }),
  Fees: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
  }),
  InteractiveMapHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  FloorPlansHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  VirtualTourHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  AmenitiesHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  PetPolicyHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  GalleryHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  NeighborhoodHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ContactHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ResidentAccessHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ScheduleATourHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ReviewsHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  PrivacyPolicyHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  SitemapHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  AccessibilityHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ApplyNowHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  ThankYouHero: defineComponentRequirements({
    tokens: ['color.text', 'font.heading', 'font.body'],
    collections: ['photo_gallery'],
  }),
  PageBlurb: defineComponentRequirements({
    tokens: ['color.text', 'color.textMuted', 'font.body'],
    fields: [
      'property_floorplans_blurb', 'property_amenities_blurb', 'property_gallery_blurb',
      'property_neighborhood_blurb', 'property_virtualtours_blurb', 'property_contactus_blurb',
      'property_reviews_blurb', 'property_pets_blurb', 'property_residentaccess_blurb',
      'property_scheduletour_blurb', 'property_interactivemap_blurb',
    ],
  }),
  Reviews: defineComponentRequirements({
    tokens: ['color.primary', 'color.text', 'color.surface', 'color.border', 'font.heading', 'font.body'],
    collections: ['reviews'],
  }),
  SpecialOfferBanner: defineComponentRequirements({
    tokens: ['color.primary', 'font.heading', 'font.body'],
    collections: ['special_offers'],
  }),
  Footer: defineComponentRequirements({
    tokens: ['color.text', 'color.border', 'font.heading', 'font.body'],
    fields: ['property_call_tracking_phone', 'property_full_address', 'property_google_place_url', 'property_mono_white_logo', 'property_mono_black_logo', 'property_company_mono_white_logo', 'property_company_mono_black_logo', 'property_company_url', 'property_facebook_url', 'property_instagram_url'],
    collections: ['office_hours'],
  }),
  Navigation: defineComponentRequirements({
    tokens: ['color.primary', 'color.secondary', 'color.text', 'color.surface', 'color.border', 'font.body'],
    fields: ['property_schedule_tour_url'],
  }),
};

export function getComponentRequirements(componentName: string): ComponentRequirements | undefined {
  return componentRequirements[componentName];
}

export function getRegisteredComponentNames(): string[] {
  return Object.keys(componentRequirements);
}
